import { useEffect, useRef, useState } from 'react';
import { ArrowLeft } from 'lucide-react';
import { Button, Input } from '@vnetwork/ui/primitives';
import { Field } from '@vnetwork/ui/forms';
import { Alert } from '@vnetwork/ui/feedback';
import { requestRegisterOtp } from '@/api/auth-flow';
import { resolveAuthError } from '@/api/auth-error';
import infoBox from './SsoNotice.module.css';
import authCta from '../RegisterPage.module.css';

const RESEND_SECONDS = 60;

/**
 * Signup email-verification step. On mount it requests a 6-digit OTP for `email`;
 * the user enters the code and `onVerify(otp)` completes the registration (the
 * parent owns the register call + its own payload). Surfaces an already-registered
 * email and offers a resend on a cooldown. Shared by the individual and business
 * signup flows so the verification UX stays identical.
 */
export function RegisterOtpStep({
  email,
  onVerify,
  onBack,
  submitLabel = 'Verify & create account',
}: {
  email: string;
  /** Complete registration with the entered code. Return an error message to
   *  display, or null on success (the parent then navigates away). */
  onVerify: (otp: string) => Promise<string | null>;
  onBack: () => void;
  submitLabel?: string;
}) {
  const [otp, setOtp] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [cooldown, setCooldown] = useState(0);

  const send = async () => {
    setSending(true);
    setError(null);
    try {
      await requestRegisterOtp(email);
      setInfo(`We sent a 6-digit code to ${email}.`);
      setCooldown(RESEND_SECONDS);
    } catch (e) {
      setError(
        resolveAuthError(e, 'Could not send the verification code. Please try again.'),
      );
    } finally {
      setSending(false);
    }
  };

  // Request the first code once on mount.
  const sentRef = useRef(false);
  useEffect(() => {
    if (sentRef.current) return;
    sentRef.current = true;
    void send();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Resend cooldown countdown.
  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  const submit = async () => {
    if (otp.trim().length < 6) {
      setError('Enter the 6-digit code from your email.');
      return;
    }
    setVerifying(true);
    setError(null);
    const msg = await onVerify(otp.trim());
    if (msg) {
      // Failure — let the user retry (or resend). On success the parent navigates
      // away, so we intentionally leave the spinner on.
      setError(msg);
      setVerifying(false);
    }
  };

  return (
    <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
      {error ? (
        <Alert tone="danger" title={error} />
      ) : info ? (
        <div className={infoBox.notice}>{info}</div>
      ) : null}

      <Field label="Verification code" required>
        {(p) => (
          <Input
            {...p}
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={6}
            placeholder="123456"
            value={otp}
            onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))}
          />
        )}
      </Field>

      {/* Resend as a subtle text link directly under the code input. */}
      <div style={{ marginTop: 'calc(-1 * var(--vn-space-2))', fontSize: 'var(--vn-font-size-sm)', color: 'var(--vn-fg-muted)' }}>
        Didn't get the code?{' '}
        <button
          type="button"
          onClick={send}
          disabled={cooldown > 0 || sending || verifying}
          style={{
            border: 'none',
            background: 'none',
            padding: 0,
            font: 'inherit',
            cursor: cooldown > 0 || sending || verifying ? 'default' : 'pointer',
            color: cooldown > 0 || sending || verifying ? 'var(--vn-fg-subtle)' : 'var(--vn-primary-text)',
            fontWeight: 'var(--vn-font-weight-semibold)',
          }}
        >
          {cooldown > 0 ? `Resend in ${cooldown}s` : sending ? 'Sending…' : 'Resend code'}
        </button>
      </div>

      <div className={authCta.actions}>
        <Button type="button" variant="secondary" size="lg" className={authCta.tall}
          startIcon={<ArrowLeft size={16} />} onClick={onBack} disabled={verifying}>
          Back
        </Button>
        <Button type="button" size="lg" className={`${authCta.ctaGradient} ${authCta.grow}`}
          onClick={submit} disabled={verifying || sending}>
          {verifying ? 'Verifying…' : submitLabel}
        </Button>
      </div>
    </div>
  );
}
